import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService, SelectItemGroup } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ListboxClickEvent, ListboxModule } from 'primeng/listbox';
import { IColumnDinamico, IGetNombreColumnas } from '../../../../shared/interfaces/database/tramites_automaticos';

export interface CampoTexto {
  id: number;
  value: string;
  order: number;
  alignment: number;
}

@Component({
  selector: 'app-act-auto-campos-texto-config',
  standalone: true,
  imports: [TableModule, CommonModule, FormsModule, ButtonModule, TranslateModule, InputTextModule, InputNumberModule, DropdownModule, TooltipModule, ListboxModule],
  template: `
  <div class="flex flex-column gap-2 w-full">
    <div class="flex justify-content-between align-items-center mb-2">
      <label class="ml-3 flex align-items-center gap-2 font-bold">
          {{'msg.camposTexto' | translate}}
          <i id="camposTexto-help" class="pi pi-info-circle" [style.color]="'#3282F3'"
            [pTooltip]="'msg.camposTextoTooltip' | translate" [escape]="false"
            tooltipPosition="top"></i>
      </label>
      <button pButton type="button" icon="pi pi-plus" [outlined]="true"
        iconPos="left" class="p-button-primary w-4rem" (click)="add()"></button>
    </div>
    <div class="flex flex-row justify-content-start mb-2 gap-2">
      <p-listbox 
        [options]="variables" 
        [group]="true" 
        [listStyle]="{ 'max-height': '300px' }" 
        [style]="{ width: '15rem' }"
        (onClick)="onSelectVariable($event)">
            <ng-template let-group pTemplate="group">
                <div class="flex align-items-center">
                    <span>{{ group.label }}</span>
                </div>
            </ng-template>
      </p-listbox>

      <p-table [value]="campos" dataKey="id" editMode="row" [tableStyle]="{'min-width': '50rem'}" [scrollable]="true" class="flex-1">
        <ng-template pTemplate="header">
            <tr>
              <th style="width:10rem">{{'common.orden' | translate}}</th>
              <th style="width:10rem">{{'common.alineamiento' | translate}}</th>
              <th>{{'common.texto' | translate}}</th>
              <th style="width:5rem"></th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-data let-editing="editing" let-ri="rowIndex">
            <tr [pEditableRow]="data">
              <td>
                <p-cellEditor>
                    <ng-template pTemplate="input">
                        <p-inputNumber inputId="integeronly" [(ngModel)]="data.order" required/>
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{data.order}}
                    </ng-template>
                </p-cellEditor>
              </td>
              <td>
                <p-cellEditor>
                    <ng-template pTemplate="input">
                        <p-dropdown 
                            [(ngModel)]="data.alignment" 
                            [options]="alignmentOptions" 
                            optionLabel="label" 
                            optionValue="value"
                            appendTo="body"
                            required>
                        </p-dropdown>
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{getAlignmentLabel(data.alignment)}}
                    </ng-template>
                </p-cellEditor>
              </td>
              <td>
                <p-cellEditor>
                    <ng-template pTemplate="input">
                        <input 
                            #campoTextoInput
                            pInputText type="text" 
                            [(ngModel)]="data.value" 
                            (focus)="setFocusedInput($event, data)" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{data.value}}
                    </ng-template>
                </p-cellEditor>
              </td>
              <td>
                <div class="flex align-items-center justify-content-center gap-2">
                    <button 
                        *ngIf="!editing" 
                        pButton 
                        pRipple 
                        type="button" 
                        pInitEditableRow 
                        icon="pi pi-pencil" 
                        (click)="onRowEditInit(data)" 
                        class="p-button-rounded p-button-text">
                    </button>
                    <button 
                        *ngIf="editing" 
                        pButton 
                        pRipple 
                        type="button" 
                        pSaveEditableRow 
                        icon="pi pi-check" 
                        (click)="onRowEditSave(data)" 
                        class="p-button-rounded p-button-text p-button-success mr-2">
                    </button>
                    <button 
                        *ngIf="editing" 
                        pButton pRipple 
                        type="button" 
                        pCancelEditableRow 
                        icon="pi pi-times" 
                        (click)="onRowEditCancel(data, ri)" 
                        class="p-button-rounded p-button-text p-button-danger">
                    </button>
                    <button 
                        *ngIf="!editing" 
                        pButton pRipple 
                        type="button" 
                        pDeleteEditableRow 
                        icon="pi pi-trash" 
                        (click)="onRowDelete(data, ri)" 
                        class="p-button-rounded p-button-text p-button-danger">
                    </button>
                </div>
              </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center p-4">
                No hay configuraciones de campos de texto. Haga clic en "Agregar" para crear uno nuevo.
              </td>
            </tr>
          </ng-template>
      </p-table>
    </div>
  </div>
  `
})
export class ActAutoCamposTextoConfigComponent {
  private focusedInput: HTMLInputElement | null = null;
  private focusedCampo: CampoTexto | null = null;

  campos: CampoTexto[] = [];
  clonedCampos: { [id: number]: CampoTexto } = {};
  idCount: number = 0;
  variables: SelectItemGroup[] = [];

  alignmentOptions = [
    { label: 'Izquierda', value: 1 },
    { label: 'Centrado', value: 2 },
    { label: 'Justificado', value: 3 }
  ];

  constructor() {
    this.variables = [
      {
        label: 'Formulario',
        value: 'formulario',
        items: [
        ]
      },
      {
        label: 'Tabla',
        value: 'tabla',
        items: [
        ]
      },
    ];
  }

  //#region tabla
  private sortCampos() {
    let sort: number = 1;
    this.campos.sort((a, b) => a.order - b.order);
    this.campos.forEach(campo => {
      campo.order = sort++;
    });
  }

  load(camposTexto: CampoTexto[]) {
    this.campos = camposTexto;
    this.campos.forEach(campo => {
      campo.id = this.idCount++;
    });
    this.sortCampos();
  }

  add() {
    this.campos.push({
      id: this.idCount++,
      value: '',
      order: this.campos.length + 1,
      alignment: 0
    });
  }

  onRowEditInit(item: CampoTexto) {
    this.clonedCampos[item.id] = { ...item };
    this.sortCampos();
  }

  onRowEditSave(item: CampoTexto) {
    delete this.clonedCampos[item.id];

    this.campos.forEach(campo => {
      if (campo.id != item.id && item.order <= campo.order) {
        campo.order = campo.order + 1;
      }
    });
    this.sortCampos();
  }

  onRowDelete(item: CampoTexto, index: number) {
    this.campos.splice(index, 1);
    this.sortCampos();
  }

  onRowEditCancel(item: CampoTexto, index: number) {
    this.campos[index] = this.clonedCampos[item.id];
    delete this.clonedCampos[item.id];
  }

  getAlignmentLabel(alignment: number): string {
    const option = this.alignmentOptions.find(opt => opt.value === alignment);
    return option ? option.label : '';
  }

  //#endregion

  //#region Variables
  setFocusedInput(event: FocusEvent, campo: CampoTexto) {
    this.focusedInput = event.target as HTMLInputElement;
    this.focusedCampo = campo;
  }
  
  onSelectVariable($event: ListboxClickEvent) {
    if (!this.focusedInput || !this.focusedCampo) return;
    const valueToInsert = $event.option?.value || $event.value;
    if (!valueToInsert) return;

    // Insert at cursor position
    const input = this.focusedInput;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const oldValue = input.value;
    const newValue = oldValue.slice(0, start) + valueToInsert + oldValue.slice(end);
    input.value = newValue;
    
    this.focusedCampo.value = newValue;
    // Move cursor after inserted text
    setTimeout(() => {
      input.focus();
      const cursorPos = start + valueToInsert.length;
      input.setSelectionRange(cursorPos, cursorPos);
    });
  }

  updateVariablesTabla(seleccion: IColumnDinamico[]) {
    this.variables = this.variables.map(variable => {
      if (variable.value === 'tabla') {
        return {
          ...variable,
          items: seleccion.map(c => ({ 
            label: c.displayName, 
            value: 't:' + c.name 
          }))
        };
      }
      return { ...variable };
    });
  }

  updateVariablesFormulario(seleccion: IGetNombreColumnas[]){
    this.variables = this.variables.map(variable => {
      if (variable.value === 'formulario') {
        return {
          ...variable,
          items: seleccion.map(c => ({ 
            label: c.controlLabel, 
            value: 'f:' + c.controlLabel 
          }))
        };
      }
      return { ...variable };
    });
  }
  //#endregion
}
